import { HTMLAttributes, useEffect, useState } from 'react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Motion from '@/components/base/Motion'
import Typography from '@/components/base/Typography'

import { opacityReveal } from './animations/opacity'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type HeroHomeProps = HTMLAttributes<HTMLElement> &
  Partial<{
    title: string
    description: string
    slides: {
      image: WPImage
      label: string
    }[]
  }>

const HeroHome = ({ title, description, slides }: HeroHomeProps) => {
  const [activeSlide, setActiveSlide] = useState(0)

  /*------------------------------
  Timer cambio slide ogni 4 secondi
  ------------------------------*/
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % (slides?.length || 1))
    }, 4000)

    return () => clearInterval(interval)
  }, [slides, activeSlide])

  return (
    <Motion animation={opacityReveal}>
      <section className="h-svh-vh relative text-white" data-hero-home>
        {slides?.length ? (
          <div className="absolute inset-0 -z-10 bg-black">
            {slides?.map((slide, index) => {
              const isActive = index === activeSlide
              return (
                <div
                  key={index}
                  className={cn(
                    'absolute inset-0 transition-opacity duration-1000 ease-out-cubic',
                    {
                      'opacity-100': isActive,
                      'opacity-0': !isActive
                    }
                  )}
                >
                  <Image
                    source={slide.image}
                    alt={slide.label}
                    size="full"
                    fit="cover"
                    fill
                  />
                </div>
              )
            })}
          </div>
        ) : null}
        <Container className="h-full bg-black/30">
          <Grid className="h-full items-end pb-[66px]">
            <div
              className={cn(
                'col-span-4',
                'md:col-span-4 md:col-start-2',
                'lg:col-span-8 lg:col-start-3'
              )}
            >
              <div className="flex flex-col gap-[15px] text-center">
                {slides?.length ? (
                  <div className="flex items-center justify-center gap-[30px]">
                    {slides?.map((slide, index) => (
                      <button
                        key={index}
                        className={cn(
                          'relative inline-block text-white transition-all duration-1000 ease-out-cubic',
                          'before:absolute before:bottom-[.1em] before:left-0 before:h-[.1em] before:w-full before:scale-x-0 before:bg-white before:transition-all before:duration-1000 before:ease-out-cubic',
                          {
                            'font-bold before:scale-x-100':
                              index === activeSlide
                          }
                        )}
                        onClick={() => setActiveSlide(index)}
                      >
                        <Typography variant="label">{slide.label}</Typography>
                      </button>
                    ))}
                  </div>
                ) : null}
                {title ? <Typography variant="h4">{title}</Typography> : null}
                {description ? (
                  <Typography variant="body">{description}</Typography>
                ) : null}
              </div>
            </div>
          </Grid>
        </Container>
      </section>
    </Motion>
  )
}

export default HeroHome
